import { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Badge,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useSalesOrders, useCustomerSearch } from '@hooks/useSales';
import type {
  SalesOrderListItem,
  SalesOrderStatus,
  SalesOrderFilters,
} from '@/types/sales';

const { RangePicker } = DatePicker;

const MD_STATUS_OPTIONS: { value: SalesOrderStatus | ''; label: string }[] = [
  { value: '', label: 'Всі статуси' },
  { value: 'Draft', label: 'Чернетка' },
  { value: 'Confirmed', label: 'Підтверджено' },
  { value: 'Processing', label: 'В обробці' },
  { value: 'PartiallyShipped', label: 'Частково відвантажено' },
  { value: 'Shipped', label: 'Відвантажено' },
  { value: 'Delivered', label: 'Доставлено' },
  { value: 'Cancelled', label: 'Скасовано' },
];

const MD_STATUS_COLOR_MAP: Record<SalesOrderStatus, string> = {
  Draft: 'default',
  Confirmed: 'processing',
  Processing: 'warning',
  PartiallyShipped: 'orange',
  Shipped: 'cyan',
  Delivered: 'success',
  Cancelled: 'error',
};

const MD_STATUS_LABEL_MAP: Record<SalesOrderStatus, string> = {
  Draft: 'Чернетка',
  Confirmed: 'Підтверджено',
  Processing: 'В обробці',
  PartiallyShipped: 'Частково відвантажено',
  Shipped: 'Відвантажено',
  Delivered: 'Доставлено',
  Cancelled: 'Скасовано',
};

interface SalesOrdersPageProps {
  onCreateOrder: () => void;
  onViewOrder: (mdOrderId: string) => void;
  onEditOrder: (mdOrderId: string) => void;
}

export default function SalesOrdersPage({
  onCreateOrder,
  onViewOrder,
  onEditOrder,
}: SalesOrdersPageProps) {
  const [mdSearch, setMdSearch] = useState('');
  const [mdCustomerFilter, setMdCustomerFilter] = useState<string | undefined>();
  const [mdStatusFilter, setMdStatusFilter] = useState<SalesOrderStatus | ''>('');
  const [mdDateRange, setMdDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(
    null
  );
  const [mdCustomerSearchText, setMdCustomerSearchText] = useState('');
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);

  const { data: mdCustomerOptions } = useCustomerSearch(mdCustomerSearchText);

  const mdFilters: SalesOrderFilters = useMemo(() => ({
    search: mdSearch || undefined,
    customerId: mdCustomerFilter,
    status: mdStatusFilter || undefined,
    dateFrom: mdDateRange?.[0]?.format('YYYY-MM-DD'),
    dateTo: mdDateRange?.[1]?.format('YYYY-MM-DD'),
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdCustomerFilter, mdStatusFilter, mdDateRange, mdPage, mdPageSize]);

  const { data: mdOrdersData, isLoading: mdIsLoading } = useSalesOrders(mdFilters);

  const mdColumns: ColumnsType<SalesOrderListItem> = [
    {
      title: 'Номер',
      dataIndex: 'number',
      key: 'number',
      width: 140,
      sorter: (mdA, mdB) => mdA.number.localeCompare(mdB.number),
    },
    {
      title: 'Клієнт',
      dataIndex: 'customerName',
      key: 'customerName',
      ellipsis: true,
    },
    {
      title: 'Дата',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      render: (mdValue: string) => dayjs(mdValue).format('DD.MM.YYYY'),
      sorter: (mdA, mdB) => mdA.orderDate.localeCompare(mdB.orderDate),
    },
    {
      title: 'Сума',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 160,
      align: 'right',
      render: (mdValue: number, mdRecord: SalesOrderListItem) =>
        `${mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })} ${mdRecord.currency}`,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      render: (mdValue: SalesOrderStatus) => (
        <Badge
          status={MD_STATUS_COLOR_MAP[mdValue] as 'default' | 'processing' | 'success' | 'error' | 'warning'}
          text={MD_STATUS_LABEL_MAP[mdValue]}
        />
      ),
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 120,
      render: (_: unknown, mdRecord: SalesOrderListItem) => (
        <Space>
          <Button type="link" size="small" onClick={() => onViewOrder(mdRecord.id)}>
            Переглянути
          </Button>
          {mdRecord.status === 'Draft' && (
            <Button type="link" size="small" onClick={() => onEditOrder(mdRecord.id)}>
              Редагувати
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Замовлення клієнтів
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={onCreateOrder}>
          Нове замовлення
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук за номером..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 220 }}
        />
        <Select
          allowClear
          showSearch
          placeholder="Клієнт"
          filterOption={false}
          onSearch={setMdCustomerSearchText}
          onChange={(mdValue: string | undefined) => {
            setMdCustomerFilter(mdValue);
            setMdPage(1);
          }}
          options={mdCustomerOptions?.map((mdCust) => ({
            value: mdCust.id,
            label: `${mdCust.code} — ${mdCust.name}`,
          }))}
          style={{ width: 250 }}
        />
        <Select
          options={MD_STATUS_OPTIONS}
          value={mdStatusFilter}
          onChange={(mdValue) => {
            setMdStatusFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 200 }}
        />
        <RangePicker
          format="DD.MM.YYYY"
          onChange={(mdDates) => {
            setMdDateRange(mdDates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null);
            setMdPage(1);
          }}
          placeholder={['Дата від', 'Дата до']}
        />
      </Space>

      <Table<SalesOrderListItem>
        columns={mdColumns}
        dataSource={mdOrdersData?.items}
        rowKey="id"
        loading={mdIsLoading}
        onRow={(mdRecord) => ({
          onClick: () => onViewOrder(mdRecord.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdOrdersData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />
    </>
  );
}
